import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
const levelDefinitions = [
    { shortLabel: "L1", barClass: "rank-bg-level1" },
    { shortLabel: "L2", barClass: "rank-bg-level2" },
    { shortLabel: "L3", barClass: "rank-bg-level3" },
    { shortLabel: "L4", barClass: "rank-bg-level4" },
];
const mapLevelTotals = (levels) => {
    const levelMap = new Map(levels?.map((entry) => [entry.Level, entry.TotalNcDays]) ?? []);
    return [1, 2, 3, 4].map((level) => levelMap.get(level) ?? 0);
};
const toPercent = (value, max) => {
    if (max <= 0) {
        return "0%";
    }
    const percent = (value / max) * 100;
    return `${Math.max(0, Math.min(100, percent))}%`;
};
const formatDays = (value) => {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};
export const NcLevelsChart = ({ options }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [hasResult, setHasResult] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [currentQuarterData, setCurrentQuarterData] = useState([0, 0, 0, 0]);
    const [previousQuarterData, setPreviousQuarterData] = useState([0, 0, 0, 0]);
    useEffect(() => {
        if (!options.isReady) {
            setHasResult(false);
            setIsLoading(false);
            setErrorMessage("");
            return;
        }
        setIsLoading(true);
        setHasResult(false);
        setErrorMessage("");
        const params = new URLSearchParams();
        params.append("year", String(options.year));
        params.append("quarter", String(options.quarter));
        options.fleetIds.forEach((id) => params.append("fleetIds", String(id)));
        fetch(`/api/insights/quarterly/nclevels?${params}`)
            .then((res) => {
            if (res.status === 204) {
                return null;
            }
            if (res.ok) {
                return res.json();
            }
            throw new Error(res.statusText);
        })
            .then((data) => {
            if (data === null) {
                setCurrentQuarterData([0, 0, 0, 0]);
                setPreviousQuarterData([0, 0, 0, 0]);
                setHasResult(false);
                return;
            }
            setCurrentQuarterData(mapLevelTotals(data.CurrentQuarter));
            setPreviousQuarterData(mapLevelTotals(data.PreviousQuarter));
            setHasResult(true);
        })
            .catch((error) => {
            console.error(error);
            setErrorMessage("Error fetching NC levels data");
        })
            .finally(() => setIsLoading(false));
    }, [options.isReady, options.year, options.quarter, options.fleetIds]);
    const valueScaleMax = Math.max(...currentQuarterData, ...previousQuarterData, 0) || 1;
    const levelRows = levelDefinitions.map((definition, index) => {
        const currentValue = currentQuarterData[index] ?? 0;
        const previousValue = previousQuarterData[index] ?? 0;
        const delta = currentValue - previousValue;
        return {
            shortLabel: definition.shortLabel,
            barClass: definition.barClass,
            currentValue: formatDays(currentValue),
            currentWidth: toPercent(currentValue, valueScaleMax),
            deltaClass: delta > 0 ? "text-danger" : delta < 0 ? "text-success" : "text-muted",
            deltaDirection: delta > 0 ? "up" : delta < 0 ? "down" : "flat",
            deltaIcon: delta > 0 ? "▲" : delta < 0 ? "▼" : "",
            deltaValue: formatDays(Math.abs(delta)),
        };
    });
    return (_jsxs("div", { className: "card shadow h-100", children: [_jsx("h3", { className: "card-header", children: "Level of NCs" }), _jsxs("div", { className: "card-body", children: [(!options.isReady || isLoading) && (_jsxs("div", { className: "text-center py-4", children: [_jsx("span", { className: "spinner-grow spinner-grow-sm", role: "status", "aria-hidden": "true" }), " Loading..."] })), options.isReady && errorMessage && (_jsx("div", { className: "alert alert-danger", role: "alert", children: errorMessage })), hasResult && !isLoading && (_jsx("div", { children: levelRows.map((row) => (_jsxs("div", { className: "d-flex", children: [_jsx("div", { className: "text-secondary pe-2", children: row.shortLabel }), _jsxs("div", { className: "position-relative flex-grow-1", children: [_jsx("div", { className: `shadow float-start soft-light-overlay ${row.barClass}`, style: { width: row.currentWidth, height: "1.25rem", maxWidth: "calc(100% - 5rem)" } }), _jsxs("div", { className: "float-start pb-2 ps-2 d-flex align-items-center gap-2", children: [_jsx("span", { children: row.currentValue }), row.deltaDirection !== "flat" && (_jsxs("span", { className: row.deltaClass, children: [_jsx("span", { children: row.deltaIcon }), _jsx("span", { children: row.deltaValue })] })), row.deltaDirection === "flat" && _jsx("span", { className: "text-muted", children: "0" })] })] })] }, row.shortLabel))) })), options.isReady && !isLoading && !hasResult && (_jsx("p", { className: "text-muted mb-0", children: "No data available for this period." }))] })] }));
};
